// Change Password page: centred card with a circular strength ring around the lock icon,
// pill-style requirement chips and inline match feedback.
import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, TextField, Button,
  IconButton, InputAdornment, CircularProgress,
} from "@mui/material";
import {
  Visibility, VisibilityOff, Lock, LockOpen,
  CheckCircle, Cancel, Check, Close,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";

// ── Design tokens (Facebook blue · grey 200 · arctic white) ───────────────────
const C = {
  blue: themeColors.primary.main,
  blueDark: themeColors.primary.dark,
  tint: "#E7F0FE",
  ink: themeColors.neutral.ink,
  sub: themeColors.neutral.sub,
  mute: themeColors.neutral.muted,
  line: "#E4E6EB",
  hover: "#F0F2F5",
  white: themeColors.neutral.surface,
  green: "#16a34a",
  greenTint: "#E8F5E9",
  red: "#E53935",
  redTint: "#FDECEA",
};

// Entrance helper. Fill mode "backwards" keeps hover effects working afterwards.
const rise = (delay = 0) => ({
  animation: "cp2Rise .5s cubic-bezier(.33,0,.2,1) " + delay + "s backwards",
});

// ── password strength helpers ─────────────────────────────────────────────────
const RULES = [
  { label: "8+ characters",     test: (p) => p.length >= 8 },
  { label: "Uppercase",         test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase",         test: (p) => /[a-z]/.test(p) },
  { label: "Number",            test: (p) => /\d/.test(p) },
  { label: "Special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const STRENGTH_META = [
  { label: "Too short", color: themeColors.error.main },
  { label: "Weak",      color: "#f97316" },
  { label: "Fair",      color: "#eab308" },
  { label: "Good",      color: "#22c55e" },
  { label: "Strong",    color: "#16a34a" },
  { label: "Very strong", color: "#166534" },
];

function getStrength(pwd) {
  if (!pwd) return 0;
  return RULES.filter((r) => r.test(pwd)).length;
}

// Ring geometry
const RING_R = 44;
const RING_LEN = 2 * Math.PI * RING_R;

// ── shared field sx ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3.5,
    bgcolor: C.white,
    transition: "box-shadow .25s ease",
    "& fieldset": { borderColor: C.line, transition: "border-color .25s ease" },
    "&:hover fieldset": { borderColor: C.blue },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(24,119,242,0.12)" },
    "&.Mui-focused fieldset": { borderColor: C.blue, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.blue },
};

function ChangePassword({ connectionString }) {
  const { user, selectedCompany } = useAuth();
  const userId       = user?.UserId          || null;
  const companyId    = selectedCompany?.CompId || null;
  const userCategory = user?.UserCategory     || null;
  const path         = connectionString;

  const [newPassword,       setNewPassword]       = useState("");
  const [confirmPassword,   setConfirmPassword]   = useState("");
  const [showNew,           setShowNew]           = useState(false);
  const [showConfirm,       setShowConfirm]       = useState(false);
  const [error,             setError]             = useState("");
  const [message,           setMessage]           = useState("");
  const [loading,           setLoading]           = useState(false);
  const navigate = useNavigate();

  const strength = useMemo(() => getStrength(newPassword), [newPassword]);
  const strengthMeta = STRENGTH_META[strength] ?? STRENGTH_META[0];
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const canSubmit = newPassword && confirmPassword && newPassword === confirmPassword && !loading;

  useEffect(() => {
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      setError("Passwords do not match!");
    } else {
      setError("");
    }
  }, [newPassword, confirmPassword]);

  const FN_HandleChangePassword = async (e) => {
    e.preventDefault();
    setMessage("");
    if (newPassword !== confirmPassword) { setError("Passwords do not match!"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${path}/api/changePassword`, {
        userId, newPassword, companyId, userCategory,
      });
      if (response.data.success) {
        setMessage("Password changed successfully!");
        setTimeout(() => navigate(-1), 2000);
      } else {
        setError("Failed to change password.");
      }
    } catch {
      setError("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const displayName = user?.EmployeeName || user?.UserName || "";
  const ringColor = message ? C.green : (newPassword ? strengthMeta.color : C.line);
  const ringFill = message ? 1 : strength / 5;

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3 },
        background: `radial-gradient(900px 420px at 50% -10%, ${C.tint} 0%, rgba(231,240,254,0) 70%), #F8FAFD`,
      }}
    >
      <style>{[
        "@keyframes cp2Rise { from { opacity: 0; transform: translate3d(0, 8px, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); } }",
        "@keyframes cp2Fade { from { opacity: 0; } to { opacity: 1; } }",
        "@keyframes cp2Shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }",
        "@keyframes cp2Pop { 0% { transform: scale(.7); opacity: 0; } 70% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }",
      ].join("\n")}</style>

      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          bgcolor: C.white,
          borderRadius: 7,
          border: `1px solid ${C.line}`,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 24px 56px rgba(24,119,242,0.10)",
          p: { xs: 3, sm: 4.5 },
          animation: "cp2Fade .45s ease backwards",
        }}
      >
        {/* ── Strength ring + lock ── */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", ...rise(0.04) }}>
          <Box sx={{ position: "relative", width: 104, height: 104 }}>
            <svg width="104" height="104" viewBox="0 0 104 104" style={{ transform: "rotate(-90deg)", display: "block" }}>
              <circle cx="52" cy="52" r={RING_R} fill="none" stroke={C.hover} strokeWidth="6" />
              <circle
                cx="52" cy="52" r={RING_R} fill="none"
                stroke={ringColor} strokeWidth="6" strokeLinecap="round"
                strokeDasharray={RING_LEN}
                strokeDashoffset={RING_LEN * (1 - ringFill)}
                style={{ transition: "stroke-dashoffset .6s cubic-bezier(.33,0,.2,1), stroke .4s ease", opacity: ringFill ? 1 : 0 }}
              />
            </svg>
            <Box
              sx={{
                position: "absolute", inset: 14, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: message ? C.greenTint : C.tint,
                color: message ? C.green : C.blue,
                transition: "background-color .4s ease, color .4s ease",
              }}
            >
              {message
                ? <Check sx={{ fontSize: 34, animation: "cp2Pop .45s ease" }} />
                : (canSubmit
                  ? <LockOpen key="open" sx={{ fontSize: 30, animation: "cp2Pop .35s ease" }} />
                  : <Lock key="shut" sx={{ fontSize: 30 }} />)}
            </Box>
          </Box>
          <Typography sx={{ mt: 1.25, minHeight: 20, fontSize: "0.78rem", fontWeight: 700, color: message ? C.green : (newPassword ? strengthMeta.color : C.mute), transition: "color .3s ease" }}>
            {message ? "Updated" : (newPassword ? strengthMeta.label : "Password strength")}
          </Typography>
        </Box>

        {/* ── Title ── */}
        <Box sx={{ textAlign: "center", mt: 1.5, mb: 3, ...rise(0.08) }}>
          <Typography sx={{ color: C.ink, fontWeight: 700, fontSize: "1.45rem", letterSpacing: "-0.4px" }}>
            Change password
          </Typography>
          <Typography sx={{ color: C.sub, fontSize: "0.88rem", mt: 0.5 }}>
            {displayName ? <>Signed in as <b style={{ color: C.ink }}>{displayName}</b></> : "Choose a strong new password"}
          </Typography>
        </Box>

        {/* Success */}
        {message && (
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 1.25,
              bgcolor: C.greenTint, border: "1px solid #bbf7d0",
              borderRadius: 3, p: 1.5, mb: 2.5,
              animation: "cp2Fade .3s ease",
            }}
          >
            <CheckCircle sx={{ color: C.green, fontSize: 20, flexShrink: 0 }} />
            <Typography fontSize="0.85rem" fontWeight={600} color="#15803d">{message}</Typography>
          </Box>
        )}

        <Box component="form" onSubmit={FN_HandleChangePassword}>

          {/* New password */}
          <Box sx={rise(0.12)}>
            <TextField
              fullWidth
              label="New password"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={fieldSx}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNew((p) => !p)} edge="end">
                        {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Requirement chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.75, mb: 2.25, ...rise(0.16) }}>
            {RULES.map((rule) => {
              const ok = rule.test(newPassword);
              return (
                <Box
                  key={rule.label}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.5,
                    px: 1.1, py: 0.45, borderRadius: 5,
                    fontSize: "0.74rem", fontWeight: ok ? 700 : 500,
                    bgcolor: ok ? C.greenTint : C.hover,
                    color: ok ? "#15803d" : C.sub,
                    border: `1px solid ${ok ? "#bbf7d0" : "transparent"}`,
                    transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
                  }}
                >
                  {ok ? <Check sx={{ fontSize: 13 }} /> : <Close sx={{ fontSize: 13, color: C.mute }} />}
                  {rule.label}
                </Box>
              );
            })}
          </Box>

          {/* Confirm password */}
          <Box sx={rise(0.2)}>
            <TextField
              fullWidth
              label="Confirm password"
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{
                ...fieldSx,
                ...(confirmPassword && {
                  "& .MuiOutlinedInput-root fieldset": {
                    borderColor: passwordsMatch ? C.green : C.red,
                  },
                }),
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {confirmPassword && (
                        passwordsMatch
                          ? <CheckCircle sx={{ fontSize: 18, color: C.green, mr: 0.5, animation: "cp2Pop .35s ease" }} />
                          : <Cancel     sx={{ fontSize: 18, color: C.red, mr: 0.5 }} />
                      )}
                      <IconButton size="small" onClick={() => setShowConfirm((p) => !p)} edge="end">
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Inline feedback (match status / server errors) */}
          <Box sx={{ minHeight: 28, mt: 1, display: "flex", alignItems: "center" }}>
            {error ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, animation: "cp2Fade .25s ease, cp2Shake .45s ease" }}>
                <Cancel sx={{ fontSize: 15, color: C.red }} />
                <Typography fontSize="0.78rem" color={themeColors.error.main} fontWeight={600}>{error}</Typography>
              </Box>
            ) : passwordsMatch ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, animation: "cp2Fade .25s ease" }}>
                <CheckCircle sx={{ fontSize: 15, color: C.green }} />
                <Typography fontSize="0.78rem" color="#15803d" fontWeight={600}>Passwords match</Typography>
              </Box>
            ) : null}
          </Box>

          {/* Submit */}
          <Box sx={{ mt: 1.5, ...rise(0.24) }}>
            <Button
              type="submit" fullWidth variant="contained"
              disabled={!canSubmit}
              sx={{
                py: 1.55,
                borderRadius: 3.5,
                fontSize: "0.95rem",
                fontWeight: 700,
                textTransform: "none",
                background: `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDark} 100%)`,
                boxShadow: "0 8px 20px rgba(24,119,242,0.28)",
                transition: "transform .2s ease, box-shadow .2s ease",
                "&:hover": { boxShadow: "0 12px 26px rgba(24,119,242,0.4)", transform: "translateY(-2px)" },
                "&:active": { transform: "translateY(0) scale(.99)" },
                "&.Mui-disabled": { background: C.hover, color: C.mute, boxShadow: "none" },
              }}
            >
              {loading
                ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={18} thickness={5} sx={{ color: "inherit" }} />Updating…</Box>
                : "Update password"}
            </Button>

            <Button
              type="button" fullWidth
              onClick={() => navigate(-1)}
              sx={{
                mt: 1, py: 1.1, borderRadius: 3.5, color: C.sub,
                fontSize: "0.88rem", fontWeight: 600, textTransform: "none",
                "&:hover": { color: C.blue, bgcolor: C.tint },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ChangePassword;
